import { Home, Building2, Car, AlertTriangle, Key, Lock, Settings, ShieldCheck } from "lucide-react";

const ServicesSection = () => {
  const services = [
    {
      id: "residential",
      title: "Residential",
      description: "Home lockouts, lock changes, rekeying, deadbolt installation, and home security upgrades.",
      icon: <Home className="w-8 h-8" />,
      image: "https://images.pexels.com/photos/20901468/pexels-photo-20901468.jpeg",
      features: ["Home Lockouts", "Lock Replacement", "Rekeying", "Smart Locks"],
      span: "md:col-span-2",
    },
    {
      id: "commercial",
      title: "Commercial",
      description: "Business security solutions including master key systems, access control, and high-security locks.",
      icon: <Building2 className="w-8 h-8" />,
      image: "https://images.pexels.com/photos/279810/pexels-photo-279810.jpeg",
      features: ["Master Key Systems", "Access Control", "Commercial Locks", "Panic Bars"],
      span: "md:col-span-1",
    },
    {
      id: "automotive",
      title: "Automotive",
      description: "Car lockouts, key programming, transponder keys, and ignition repair for all vehicle makes.",
      icon: <Car className="w-8 h-8" />,
      image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d",
      features: ["Car Lockouts", "Key Programming", "Transponder Keys", "Ignition Repair"],
      span: "md:col-span-1",
    },
    {
      id: "emergency",
      title: "24/7 Emergency",
      description: "Locked out at 3 AM? Our emergency team is ready to help you anytime, day or night.",
      icon: <AlertTriangle className="w-8 h-8" />,
      image: "https://images.pexels.com/photos/279810/pexels-photo-279810.jpeg?auto=compress&cs=tinysrgb&w=1200",
      features: ["24/7 Availability", "Fast Response", "All Lock Types", "No Extra Fees"],
      span: "md:col-span-2",
      highlight: true,
    },
  ];

  return (
    <section
      id="services"
      className="py-20 md:py-32 bg-slate-900"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-amber-400 mb-4 block">
            Our Services
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Complete Locksmith Solutions
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            From home lockouts to commercial security systems, we provide comprehensive locksmith services for Saskatoon and surrounding areas.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        {/* Additional Services */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniService icon={<Key className="w-5 h-5" />} title="Key Cutting" />
          <MiniService icon={<Lock className="w-5 h-5" />} title="Lock Repair" />
          <MiniService icon={<Settings className="w-5 h-5" />} title="Lock Installation" />
          <MiniService icon={<ShieldCheck className="w-5 h-5" />} title="Security Audits" />
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service }) => (
  <div
    className={`service-card bento-card ${service.span} ${
      service.highlight ? "row-span-1" : ""
    } relative h-80 md:h-96 rounded-sm overflow-hidden group cursor-pointer`}
    data-testid={`service-${service.id}`}
  >
    {/* Background Image */}
    <img
      src={service.image}
      alt={service.title}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
    />

    {/* Content Overlay */}
    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>

    {/* Content */}
    <div className="bento-card-content absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
      {/* Icon */}
      <div
        className={`w-14 h-14 rounded-sm flex items-center justify-center mb-4 ${
          service.highlight
            ? "bg-amber-500 text-slate-900"
            : "bg-blue-900 text-white"
        }`}
      >
        {service.icon}
      </div>

      {/* Title */}
      <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">
        {service.title}
      </h3>

      {/* Description */}
      <p className="text-slate-300 text-sm md:text-base mb-4 line-clamp-2">
        {service.description}
      </p>

      {/* Features */}
      <div className="flex flex-wrap gap-2">
        {service.features.slice(0, 3).map((feature, index) => (
          <span
            key={index}
            className="text-xs bg-white/20 text-white px-3 py-1 rounded-sm"
          >
            {feature}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const MiniService = ({ icon, title }) => (
  <div className="flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-sm p-4 hover:border-amber-500 transition-colors">
    <div className="text-amber-400">{icon}</div>
    <span className="text-slate-300 font-medium text-sm">{title}</span>
  </div>
);

export default ServicesSection;
